import React,{useState,useEffect,useRef} from "react";
import API,{setToken,getToken,removeToken} from "./api";
import {C,CC,SC,NC,NI,LANG,T,fmt,today,todayStr,monthStart,monthEnd,
  fyStart,fyEnd,last7,last30,filterByDate,getFYList,gCalc,dlCSV,numToWords,canAccess,getPlanLimit,PLAN_LIMITS,PLAN_NAMES,UPGRADE_MSG} from "./utils/constants";
import {Bdg,CB,Card,Sc,Row,Btn,Inp,Sel,Bar,Modal} from "./components/UI";
import Login from "./components/Login";
import {pInv,pInvBulk,pPur,pNote,pReceipt,waShare,AddProdToInvoice,InvModal} from "./components/Invoice";
import {NewOrderPage,NewPurPage} from "./components/OrderForms";
import BankRecon from "./components/BankRecon";
import PhotoScanModal from "./components/PhotoScan";
import PageContent from "./pages/PageContent";
import SuperAdminPanel from "./components/SuperAdminPanel";

export default function App(){
  // ── States ──────────────────────────────────────────────
  const [user,setUser]=useState(null);
  const userRef=React.useRef(null);
  const [deferredPrompt,setDeferredPrompt]=useState(null);
  const [showInstall,setShowInstall]=useState(false);
  const [superAdmin,setSuperAdmin]=useState(null);

  // Support impersonation — Super Admin logging in as a distributor's admin, temporarily
  const impersonateTenant=async(tenantId)=>{
    try{
      const res=await API.post(`/tenants/${tenantId}/impersonate`,{});
      if(res?.token){
        // Stash the super admin's own token so we can return to that view later
        sessionStorage.setItem("gd_sa_token_backup", getToken()||"");
        setToken(res.token);
        clearAllState();
        setSuperAdmin(null);
        setUser(res.user);
        loadData(false,res.user);
        showToast(`👁️ Viewing as ${res.tenant_name||res.user.name} (support mode)`);
      }
    }catch(e){ showToast("❌ Failed to impersonate: "+(e.message||"Server error"),"err"); }
  };

  const returnToSuperAdmin=()=>{
    const saToken=sessionStorage.getItem("gd_sa_token_backup");
    if(!saToken){ showToast("❌ No Super Admin session to return to","err"); return; }
    sessionStorage.removeItem("gd_sa_token_backup");
    setToken(saToken);
    clearAllState();
    setUser(null);
    try{
      const payload=JSON.parse(atob(saToken.split('.')[1]));
      setSuperAdmin(payload);
      loadTenants();
    }catch{}
  };
  const [page,setPage]=useState("dash");
  const [subPage,setSubPage]=useState(null);
  const [menuOpen,setMenuOpen]=useState(false);
  const [modal,setModal]=useState(null);
  const [tenantPlan,setTenantPlan]=useState(()=>localStorage.getItem("gd_plan")||"enterprise");
  const [pricing,setPricing]=useState(null);
  const [offers,setOffers]=useState([]);
  const [form,setForm]=useState({});
  const [toast,setToast]=useState(null);
  const [lang,setLang]=useState(()=>localStorage.getItem("gd_lang")||"en");
  const [selFY,setSelFY]=useState(()=>{const d=new Date();const y=d.getMonth()<3?d.getFullYear()-1:d.getFullYear();return y;});
  const [dateFrom,setDateFrom]=useState("");
  const [dateTo,setDateTo]=useState("");
  const [gTab,setGTab]=useState("gstr1");
  const [saTab,setSaTab]=useState("tenants");
  const [announcement,setAnnouncement]=useState("");
  const [editComp,setEditComp]=useState(null);
  const [payOid,setPayOid]=useState("");
  const [payForm,setPayForm]=useState({cust:"",amt:"",method:"Cash",note:"",account_id:""});
  const [nItems,setNItems]=useState([]);
  const [payAmt,setPayAmt]=useState("");
  const [payMethod2,setPayMethod2]=useState("Cash");
  const [payingCust,setPayingCust]=useState(null);
  const [dbOtp,setDbOtp]=useState("");
  const [dbOtpOrderId,setDbOtpOrderId]=useState(null);
  const [saForm,setSaForm]=useState({});
  const [saMsg,setSaMsg]=useState("");
  const [aiInsight,setAiInsight]=useState(null);
  const [aiLoading,setAiLoading]=useState(false);
  const [aiPurSug,setAiPurSug]=useState(null);
  const [cf,setCf]=useState("all");
  const [prev,setPrev]=useState(null);
  const [beats,setBeats]=useState([]);
  const [batches,setBatches]=useState([]);
  const [notifications,setNotifications]=useState([]);
  const [showNotifPanel,setShowNotifPanel]=useState(false);
  const [isOffline,setIsOffline]=useState(!navigator.onLine);
  // The top bar (banners + header) is now position:fixed so it can NEVER scroll away,
  // even if some future page introduces an ancestor with overflow set (the classic way
  // position:sticky quietly breaks). Since the banners are conditional, its height
  // changes — this measures the real rendered height so the page content below can be
  // pushed down by exactly that much, instead of hiding underneath the fixed bar.
  const topBarRef=useRef(null);
  const [topBarHeight,setTopBarHeight]=useState(50);
  useEffect(()=>{
    const measure=()=>{ if(topBarRef.current) setTopBarHeight(topBarRef.current.offsetHeight); };
    measure();
    const ro=new ResizeObserver(measure);
    if(topBarRef.current) ro.observe(topBarRef.current);
    window.addEventListener("resize",measure);
    return()=>{ ro.disconnect(); window.removeEventListener("resize",measure); };
  },[]);
  const unreadCount=notifications.filter(n=>!n.is_read).length;
  const [accounts,setAccounts]=useState([]);
  const [expenses,setExpenses]=useState([]);
  const [cheques,setCheques]=useState([]);
  const [suppPays,setSuppPays]=useState([]);
  const [tenants,setTenants]=useState([]);
  const [trialExpired,setTrialExpired]=useState(false);
  const [trialDaysLeft,setTrialDaysLeft]=useState(null);
  const [tenantInfo,setTenantInfo]=useState(null);
  const [myCompany,setMyCompany]=useState({name:"",owner:"",gstin:"",phone:"",email:"",address:"",msme:"",logo:"",qr:"",bank:"",acc:"",ifsc:"",upi:"",terms1:"",terms2:"",terms3:"",dueDays:"30",paperSize:"A4 (Full Page)",printCopies:"1 Copy",showDiscount:true,showMRP:true,showHSN:true,showOverallDisc:true});

  // ── Data States ──────────────────────────────────────────
  const [companies,setCompanies]=useState([]);
  const [custs,setCusts]=useState([]);
  const [supps,setSupps]=useState([]);
  const [prods,setProds]=useState([]);
  const [orders,setOrders]=useState([]);
  const [pays,setPays]=useState([]);
  const [purs,setPurs]=useState([]);
  const [notes,setNotes]=useState([]);
  const [users,setUsers]=useState([]);

  // ── Helpers ──────────────────────────────────────────────
  const nav=(p)=>{setPage(p);setMenuOpen(false);setSubPage(null);setPrev(null);};
  const showToast=(m,t="ok")=>{setToast({m,t});setTimeout(()=>setToast(null),3000);};
  const gc=id=>companies.find(c=>c.id===id);
  const tSales=orders.reduce((s,o)=>s+(+o.total||0),0);
  const tPaid=pays.reduce((s,p)=>s+(+p.amt||0),0);
  const tDue=custs.reduce((s,c)=>s+(+c.bal||0),0);
  const lowS=prods.filter(p=>p.stock<=p.min);
  // Batches expiring within 30 days (or already expired), qty>0 only
  const expiringBatches=batches.filter(b=>{
    if(!b.expiry_date||(+b.qty||0)<=0) return false;
    const days=(new Date(b.expiry_date)-new Date())/(1000*60*60*24);
    return days<=30;
  }).sort((a,b)=>new Date(a.expiry_date)-new Date(b.expiry_date));
  const isA=user?.role==="admin";
  React.useEffect(()=>{ userRef.current=user; },[user]);
  const isDB=user?.role==="delivery";

  // ── Load Data ────────────────────────────────────────────
  // Clear all data state — called on logout and before loading new user's data
  const clearAllState=()=>{
    setCompanies([]);setCusts([]);setSupps([]);setProds([]);
    setOrders([]);setPays([]);setPurs([]);setNotes([]);
    setUsers([]);setExpenses([]);setCheques([]);setAccounts([]);setBeats([]);setBatches([]);setSuppPays([]);
    setMyCompany({name:"",owner:"",gstin:"",phone:"",email:"",address:"",msme:"",logo:"",qr:"",bank:"",acc:"",ifsc:"",upi:"",terms1:"",terms2:"",terms3:"",dueDays:"30",paperSize:"A4 (Full Page)",printCopies:"1 Copy",showDiscount:true,showMRP:true,showHSN:true,showOverallDisc:true});
    setTenantInfo(null);setTenantPlan("trial");setTrialExpired(false);setTrialDaysLeft(null);setPricing(null);setOffers([]);
    setAiInsight(null);setAiPurSug(null);setAiLoading(false);setNotifications([]);
    localStorage.removeItem("gd_myco");
    localStorage.removeItem("gd_plan");
  };

  // ── Notifications ────────────────────────────────────────────
  const loadNotifications=async()=>{
    try{
      const d=await API.getNotifications();
      if(Array.isArray(d)) setNotifications(d);
    }catch(e){}
  };
  const markNotifRead=async(id)=>{
    setNotifications(prev=>prev.map(n=>n.id===id?{...n,is_read:1}:n));
    try{ await API.markNotificationRead(id); }catch(e){}
  };
  const markAllNotifsRead=async()=>{
    setNotifications(prev=>prev.map(n=>({...n,is_read:1})));
    try{ await API.markAllNotificationsRead(); }catch(e){}
  };
  // Registers the service worker — this powers BOTH offline mode (caching, queue, 
  // sync) AND push notifications. Runs unconditionally, regardless of whether the 
  // user grants notification permission, since offline billing must work either way.
  const registerServiceWorker=async()=>{
    if(!("serviceWorker" in navigator)) return null;
    try{
      const reg=await navigator.serviceWorker.register("/service-worker.js");
      // Actively re-check for a newer service-worker.js every 5 minutes while the
      // app is open — the browser's own background check is much slower (often
      // only on navigation), so this makes a fresh deployment surface faster.
      setInterval(()=>{ reg.update().catch(()=>{}); }, 5*60*1000);
      return reg;
    }catch(e){ console.log("Service worker registration failed:", e.message); return null; }
  };

  // "New version available" banner — fires when a freshly-deployed service worker
  // takes over from an older one on an ALREADY-OPEN tab. Since the new JS bundle is
  // only loaded into memory on a full page reload, users need a clear, one-tap way
  // to pick that up instead of quietly sitting on stale code until they notice
  // something's off (or have to manually clear their browser's cache).
  const [updateAvailable,setUpdateAvailable]=useState(false);
  useEffect(()=>{
    if(!("serviceWorker" in navigator)) return;
    // Capture whether a service worker was ALREADY controlling this page when the
    // app first mounted. If yes, any controllerchange that fires later this session
    // means a NEWER service worker just took over — a genuine update. If no (this is
    // literally the first-ever activation for this browser), skip the banner.
    const hadControllerAtStart=!!navigator.serviceWorker.controller;
    const onControllerChange=()=>{
      if(hadControllerAtStart) setUpdateAvailable(true);
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    return()=>navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
  },[]);

  // Subscribes THIS device for push notifications — safe to call multiple times
  // (no-op if unsupported/denied). Does NOT gate service worker registration —
  // that already happened independently via registerServiceWorker() above.
  const enablePushNotifications=async()=>{
    try{
      if(!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      const permission=await Notification.requestPermission();
      if(permission!=="granted") return;
      const reg=await navigator.serviceWorker.ready;
      const {publicKey}=await API.getVapidPublicKey();
      if(!publicKey) return;
      const urlBase64ToUint8Array=(base64String)=>{
        const padding="=".repeat((4-base64String.length%4)%4);
        const base64=(base64String+padding).replace(/-/g,"+").replace(/_/g,"/");
        const rawData=window.atob(base64);
        return Uint8Array.from([...rawData].map(c=>c.charCodeAt(0)));
      };
      let sub=await reg.pushManager.getSubscription();
      if(!sub){
        sub=await reg.pushManager.subscribe({userVisibleOnly:true, applicationServerKey:urlBase64ToUint8Array(publicKey)});
      }
      await API.subscribePush(sub.toJSON());
    }catch(e){ console.log("Push setup skipped:", e.message); }
  };

  // Nudges the service worker to flush its offline write-queue back to the server.
  // Tries the Background Sync API first (works even if the tab isn't open),
  // and always ALSO sends a direct message as a fallback since Background Sync
  // isn't supported at all on iOS Safari.
  const triggerOfflineSync=async()=>{
    if(!("serviceWorker" in navigator)) return;
    try{
      const reg=await navigator.serviceWorker.ready;
      if("sync" in reg){
        try{ await reg.sync.register("d2d-sync"); }catch(e){}
      }
      reg.active?.postMessage({type:"FORCE_SYNC"});
    }catch(e){ console.log("Sync trigger skipped:", e.message); }
  };

  const loadData=async(silent=false, currentUser=null)=>{
    try{
      if(!silent) showToast("Loading...","ok");
      // Promise.allSettled — ek route fail ho (e.g. adminOnly returns 403 for staff)
      // toh baaki sab data phir bhi load hoga
      const [rco,rcu,rsu,rpr,ror,rpa,rpu,rno,rus,rex,rch,rac,rbt,rba,rsp]=await Promise.allSettled([
        API.getCompanies(),API.getCustomers(),API.getSuppliers(),
        API.getProducts(),API.getOrders(),API.getPayments(),
        API.getPurchases(),API.getNotes(),API.getUsers(),
        API.getExpenses(),API.getCheques(),API.getAccounts(),API.getBeats(),API.getBatches(),API.getSupplierPayments()
      ]);
      const ok=(r)=>r.status==="fulfilled"&&r.value;
      const co=ok(rco)?rco.value:null;
      const cu=ok(rcu)?rcu.value:null;
      const su=ok(rsu)?rsu.value:null;
      const pr=ok(rpr)?rpr.value:null;
      const or=ok(ror)?ror.value:null;
      const pa=ok(rpa)?rpa.value:null;
      const pu=ok(rpu)?rpu.value:null;
      const no=ok(rno)?rno.value:null;
      const us=ok(rus)?rus.value:null;
      const ex=ok(rex)?rex.value:null;
      const ch=ok(rch)?rch.value:null;
      const ac=ok(rac)?rac.value:null;
      const bt=ok(rbt)?rbt.value:null;
      const ba=ok(rba)?rba.value:null;
      const sp=ok(rsp)?rsp.value:null;
      // Load tenant plan
      try{
        const info=await API.get("/tenant/info");
        if(info?.plan){
          setTenantPlan(info.plan);
          localStorage.setItem("gd_plan",info.plan);
        }
      }catch(e){ setTenantPlan("enterprise"); }

      // Load live subscription pricing (set by Super Admin)
      try{
        const p=await API.get("/pricing");
        if(p&&Object.keys(p).length) setPricing(p);
      }catch(e){}

      // Load active offers/coupons (set by Super Admin)
      try{
        const o=await API.get("/offers");
        if(Array.isArray(o)) setOffers(o);
      }catch(e){}

      // Load My Company from server
      try{
        const myco = await API.get("/my-company");
        if(myco && myco.id){
          setMyCompany(prev=>({...prev,...myco}));
          localStorage.setItem("gd_myco", JSON.stringify({...myco}));
        }
      }catch(e){ console.log("myco load:", e); }
      // Staff company filter — if staff has assigned company, only show that company's data
      const activeUser = currentUser || userRef.current || user;
      const staffCid = (activeUser?.role!=="admin" && activeUser?.company_id) ? activeUser.company_id : null;

      const allCos = Array.isArray(co)?co.map(c=>({...c,short:c.short_name||c.short||c.name?.slice(0,3),sp:c.sale_price,pp:c.purchase_price,gst:c.gst_rate||18,color:c.color||"#2563EB",ctr:c.invoice_counter||0})):[];
      setCompanies(staffCid ? allCos.filter(c=>c.id===staffCid) : allCos);
      if(Array.isArray(cu))setCusts(cu.map(c=>({...c,bal:+c.balance||0,paid:+c.total_paid||0,loc:c.location||"",credit_limit:+c.credit_limit||0})));
      if(Array.isArray(su))setSupps(su.map(s=>({...s,bal:+s.balance||0,paid:+s.total_paid||0,cid:s.company_id})));
      const allProds = Array.isArray(pr)?pr.map(p=>({...p,sp:+p.sp||+p.sale_price||0,pp:+p.pp||+p.purchase_price||0,gst:+p.gst||+p.gst_rate||18,cid:p.cid||p.company_id,min:+p.min||+p.min_stock||10})):[];
      setProds(staffCid ? allProds.filter(p=>p.cid===staffCid) : allProds);
      const allOrders = Array.isArray(or)?or.map(o=>({...o,cust:o.customer_name,phone:o.customer_phone,gstin:o.customer_gstin,inv:o.invoice_no,cid:o.company_id,sub:+o.subtotal,tax:+o.taxable,gst:+o.gst_amt,gamt:+o.gst_amt,total:+o.total,paid:+o.paid,odisc:+o.overall_disc||0,odp:+o.overall_disc_pct||0,gt:o.gst_type,by:o.added_by,items:o.items||[]})):[];
      setOrders(staffCid ? allOrders.filter(o=>o.cid===staffCid) : allOrders);

      const allPays = Array.isArray(pa)?pa.map(p=>({...p,cust:p.customer_name,inv:p.invoice_no,cid:p.company_id,amt:+p.amount,method:p.method||"Cash"})):[];
      setPays(staffCid ? allPays.filter(p=>p.cid===staffCid) : allPays);

      const allPurs = Array.isArray(pu)?pu.map(p=>({...p,supp:p.supplier_name,cid:p.company_id,sub:+p.subtotal,gstAmt:+p.gst_amt,gst:+p.gst_amt,total:+p.total,paid:+p.paid,items:(p.items||[]).map(it=>({...it,pid:it.product_id,name:it.product_name,cp:+it.cost_price||0,qty:+it.qty,gst:+it.gst_rate||18}))})):[];
      setPurs(staffCid ? allPurs.filter(p=>p.cid===staffCid) : allPurs);
      if(Array.isArray(no))setNotes(no.map(n=>({...n,noteNo:n.note_no,nt:n.type,cid:n.company_id,party:n.party,refInv:n.ref_invoice,gt:n.gst_type,taxable:+n.taxable,gst:+n.gst_amt,total:+n.total,items:n.items||[]})));
      if(Array.isArray(us))setUsers(us);
      if(Array.isArray(ex))setExpenses(ex.map(e=>({...e,amt:+e.amount||0})));
      if(Array.isArray(ac))setAccounts(ac.map(a=>({...a,bal:+a.current_balance||+a.opening_balance||0})));
      if(Array.isArray(bt))setBeats(bt);
      if(Array.isArray(ba))setBatches(ba);
      if(Array.isArray(sp))setSuppPays(sp);
      if(Array.isArray(ch))setCheques(ch.map(c=>({...c,amt:+c.amount||0})));

      // ── Check tenant trial/plan status ────────────────────────
      try{
        const tInfo = await API.get("/tenant/info");
        if(tInfo && tInfo.plan){
          setTenantInfo(tInfo);
          if(tInfo.trial_end){
            const days = Math.ceil((new Date(tInfo.trial_end)-new Date())/(864e5));
            setTrialDaysLeft(days);
            if(tInfo.status==="expired"||(days<0&&tInfo.plan==="trial")){
              setTrialExpired(true);
            } else {
              setTrialExpired(false);
            }
          }
        }
      }catch(e){ console.log("Tenant check:", e.message); }
    }catch(e){console.error("Load error:",e.message);}
  };

  const loadTenants=async()=>{
    try{const d=await API.get("/tenants");setTenants(Array.isArray(d)?d:[]);}catch(e){console.log(e);}
  };

  useEffect(()=>{
    // Register the service worker IMMEDIATELY, regardless of login state or
    // notification permission — this is what powers offline billing, so it
    // must not depend on the user saying "yes" to an unrelated push prompt.
    registerServiceWorker();

    const token=getToken();
    if(token){
      try{
        const payload=JSON.parse(atob(token.split('.')[1]));
        if(payload.role==="superadmin"){setSuperAdmin(payload);loadTenants();}
        else{clearAllState();setUser(payload);loadData(false,payload);}
      }catch{removeToken();}
    }
    // PWA Install prompt
    window.addEventListener('beforeinstallprompt',(e)=>{
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    });
    window.addEventListener('appinstalled',()=>{
      setShowInstall(false);
      setDeferredPrompt(null);
    });
  },[]);

  // Notifications — poll every 45s while a tenant is logged in, and register for push once
  useEffect(()=>{
    if(!user) return;
    loadNotifications();
    enablePushNotifications();
    const interval=setInterval(loadNotifications, 45000);
    return()=>clearInterval(interval);
  },[user?.username]);

  // Listen for messages from the service worker — notification taps and offline-sync results
  useEffect(()=>{
    if(!("serviceWorker" in navigator)) return;
    const handler=(event)=>{
      if(event.data?.type==="NOTIFICATION_CLICK" && event.data.link){
        nav(event.data.link);
        loadNotifications();
      } else if(event.data?.type==="SYNC_COMPLETE"){
        if(event.data.synced>0) showToast(`✅ ${event.data.synced} offline change(s) synced!`);
        loadData(true);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return()=>navigator.serviceWorker.removeEventListener("message", handler);
  },[]);

  // Internet wapas aane pe: offline mein jo bhi changes save hue the, unhe server pe bhejo
  useEffect(()=>{
    const goOnline=()=>{
      setIsOffline(false);
      showToast("🌐 Back online — syncing your offline changes...");
      triggerOfflineSync();
    };
    const goOffline=()=>setIsOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    // App khulte hi ek baar bhi check kar lo — pichhli offline session ka pending data ho sakta hai
    if(navigator.onLine) triggerOfflineSync();
    return()=>{
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  },[]);

  // Prevent browser back button from closing app
  useEffect(()=>{
    const handler=()=>{
      if(subPage){setSubPage(null);}
      else if(modal){setModal(null);}
      else if(page!=="dash"){setPage("dash");}
      window.history.pushState(null,"",window.location.href);
    };
    window.history.pushState(null,"",window.location.href);
    window.addEventListener("popstate",handler);
    return()=>window.removeEventListener("popstate",handler);
  },[page,subPage,modal]);

  // ── AI Functions ─────────────────────────────────────────
  const callAI=async(prompt)=>{
    // AI calls now go through server proxy (key never exposed to frontend)
    const res = await API.post("/ai/insights", {orders, pays, custs, prods, companies});
    return res.insight || "";
  };
  
  const getAIInsights=async()=>{
    setAiLoading(true);
    try{
      const res = await API.post("/ai/insights", {
        orders: filterByDate(orders, monthStart(), monthEnd()),
        pays: filterByDate(pays, monthStart(), monthEnd()),
        custs,
        prods,
        companies
      });
      setAiInsight(res.insight || "No insights available.");
    }catch(e){
      setAiInsight("❌ AI unavailable: "+(e.message||"Server error"));
    }finally{ setAiLoading(false); }
  };
  
  const getSmartPurchaseSuggestions=async()=>{
    const low=prods.filter(p=>p.stock<=p.min*2).slice(0,10);
    if(!low.length){showToast("✅ All products are well stocked!");return;}
    setAiLoading(true);
    try{
      const res = await API.post("/ai/purchase", {prods, purs});
      setAiPurSug(res.suggestion || "No suggestions available.");
    }catch(e){
      showToast("❌ AI unavailable: "+(e.message||"Server error"),"err");
    }finally{ setAiLoading(false); }
  };
  
  const sendDailyReport=()=>{
    const ts=filterByDate(orders,todayStr(),todayStr()).reduce((s,o)=>s+(+o.total||0),0);
    const tc=filterByDate(pays,todayStr(),todayStr()).reduce((s,p)=>s+(+p.amt||0),0);
    const to=filterByDate(orders,todayStr(),todayStr()).length;
    const msg=encodeURIComponent(`📊 *Daily Report*\n📅 ${todayStr()}\n\n🛒 Orders: ${to}\n💰 Sales: ₹${fmt(ts)}\n✅ Collection: ₹${fmt(tc)}\n⚠️ Due: ₹${fmt(tDue)}\n📦 Low Stock: ${lowS.length}\n\n🏢 ${myCompany?.name||"DawnToDusk Pro"}`);
    window.open(`https://wa.me/?text=${msg}`,"_blank");
  };

  const sendRenewalReminder=(custName, phone, planName, daysLeft)=>{
    if(!phone) return;
    const ph = phone.replace(/\D/g,"");
    const hl = JSON.parse(localStorage.getItem("sa_helpline")||"{}");
    const msg=encodeURIComponent(`🔔 *Subscription Renewal Reminder*\n\nDear Customer,\n\nYour DawnToDusk Pro *${planName}* plan expires in ${daysLeft} days.\n\n✅ To renew:\n📞 ${hl.phone||""}\n💬 WhatsApp: ${hl.whatsapp||""}\n\nThank you! 🙏\nTeam DawnToDusk Pro`);
    window.open(`https://wa.me/91${ph}?text=${msg}`,"_blank");
  };
  const sendPaymentReminder=(cust)=>{
    const phone=(cust.phone||"").replace(/\D/g,"");
    if(!phone){showToast("No phone number found!","err");return;}
    const msg=encodeURIComponent(`🙏 *Payment Reminder*\n\nDear *${cust.name}*,\n⚠️ Outstanding Balance: *₹${fmt(cust.bal||0)}*\n\nWe kindly request you to clear the payment at your earliest convenience.\n\nThank you! 🙏\n🏢 ${myCompany?.name||"DawnToDusk Pro"}`);
    window.open(`https://wa.me/91${phone}?text=${msg}`,"_blank");
  };

  // ── Order Handler ─────────────────────────────────────────
  const handleOrder=async({cid,cust,cobj,gt,items,odp,odisc,tax,gamt,total})=>{
    const co=companies.find(c=>c.id===cid);
    const n=(co?.ctr||0)+1;
    const pf=co?.postfix?`-${co.postfix}`:"";
    const inv=`${co?.prefix||"INV"}${pf}-${String(n).padStart(4,"0")}`;
    setCompanies(companies.map(c=>c.id===cid?{...c,ctr:n}:c));
    const id="ORD"+Date.now();
    const sub=items.reduce((s,i)=>s+(i.price-i.disc)*i.qty,0);
    const o={id,inv,cid,cust,phone:cobj?.phone||"",gstin:cobj?.gstin||"",
      date:today(),sub,odp,odisc,tax,gst:gamt,total,paid:0,
      status:"Pending",gt,items,added_by:user?.username||"admin"};
    setOrders([o,...orders]);
    // Update customer balance locally - server will sync on loadData
    setCusts(custs.map(c=>c.name===cust?{...c,bal:(c.bal||0)+(+total||0),paid:(c.paid||0)}:c));
    setSubPage(null);
    try{
      await API.createOrder({...o,inv:o.inv,cust:o.cust,gamt:o.gst,invoice_counter:n});
      await loadData(true);
      showToast(`✅ Order ${inv} saved!`);
    }catch(e){
      console.error("Order save error:",e.message||e);
      // Rollback local state
      setOrders(orders);
      setCompanies(companies);
      setCusts(custs);
      showToast("❌ Order save failed: "+(e.message||"Server error"),"err");
    }
  };

  const handlePur=async(pd)=>{
    const id="PUR"+Date.now();
    const p={...pd,id,date:pd.date||today(),status:"Received",by:user?.username||"admin",supp_inv_no:pd.suppInvNo||""};
    setPurs([p,...purs]);
    // Stock increases by BOTH billed qty AND free/bonus qty (e.g. supplier scheme "10+2")
    p.items?.forEach(it=>setProds(prods.map(pr=>pr.id===it.pid?{...pr,stock:(pr.stock||0)+(+it.qty||0)+(+it.free||0)}:pr)));
    setSupps(supps.map(s=>s.name===p.supp?{...s,bal:(s.bal||0)+(p.total-(p.paid||0))}:s));
    setSubPage(null);
    try{
      await API.createPurchase(p);
      await loadData(true);
      showToast("✅ Purchase MySQL mein save!");
    }catch(e){
      console.error("Purchase save error:",e.message||e);
      showToast("⚠️ Purchase local save hua, server error: "+( e.message||"unknown"),"err");
    }
  };

  const addPay=async(custName,amt,method,oid,accountId)=>{
    const pid="PAY"+Date.now();
    const ord=oid?orders.find(o=>o.id===oid):null;
    const cid=ord?.cid||companies[0]?.id||"";
    const inv=ord?.inv||"";
    const p={id:pid,oid:oid||"MANUAL",inv,cid,cust:custName,amt:+amt,date:today(),method:method||"Cash",by:user?.username||"admin",added_by:user?.username||"admin"};
    // API payload — account_id tells the server WHICH cash/bank account this payment
    // actually went into. Without this, the server silently falls back to the default
    // cash account, so a payment marked "Bank" would never show up in the bank balance.
    const apiPay={oid:p.oid,inv:p.inv,cid:p.cid,cust:p.cust,amount:p.amt,date:p.date,method:p.method,note:"",account_id:accountId||""};
    setPays([p,...pays]);
    if(oid&&oid!=="MANUAL"){
      setOrders(orders.map(o=>o.id===oid?{...o,paid:(o.paid||0)+(+amt),status:(o.paid||0)+(+amt)>=o.total?"Paid":"Partial"}:o));
    }
    setCusts(custs.map(c=>c.name===custName?{...c,bal:Math.max(0,(c.bal||0)-(+amt)),paid:(c.paid||0)+(+amt)}:c));
    try{
      await API.createPayment(apiPay);
      await loadData(true);
      showToast("✅ Payment recorded!");
      // Receipt open karo
      const cust=custs.find(c=>c.name===custName);
      const phone=(cust?.phone||"").replace(/[^0-9]/g,"");
      const totalDue=Math.max(0,(cust?.bal||0)-(+amt));
      if(pReceipt){
        pReceipt({
          id:pid,cust:custName,amt:+amt,
          method:method||"Cash",date:today(),
          inv:inv||"",note:"",phone:phone,due:totalDue
        }, myCompany||{});
      }
    }catch(e){
      console.error("Pay err:",e);
      // Rollback local state
      setPays(pays);
      setOrders(orders);
      setCusts(custs);
      showToast("❌ Payment failed: "+(e.message||"Server error"),"err");
    }
  };

  // ── Menus ─────────────────────────────────────────────────
  const ca=(f)=>canAccess(tenantPlan,f);
  const [expandedGroups,setExpandedGroups]=useState({Sales:true,Purchase:false,Master:false,Accounts:false,Reports:false,Settings:false});
  const toggleGroup=(g)=>setExpandedGroups(prev=>({...prev,[g]:!prev[g]}));

  // ── Grouped Menu ──────────────────────────────────────
  const ADMIN_MENU_GROUPS=[
    {
      group:"Sales",icon:"📦",
      items:[
        {id:"orders",i:"📦",l:T(lang,"orders")},
        {id:"pays",i:"💰",l:T(lang,"payments")},
        ...(ca("notes")?[{id:"notes",i:"📋",l:T(lang,"notes")}]:[]),
      ]
    },
    {
      group:"Purchase",icon:"🛒",
      items:[
        ...(ca("purs")?[{id:"purs",i:"🛒",l:T(lang,"purchases")}]:[]),
        ...(ca("supps")?[{id:"supps",i:"🏭",l:T(lang,"suppliers")}]:[]),
      ]
    },
    {
      group:"Master",icon:"👥",
      items:[
        {id:"custs",i:"👥",l:T(lang,"customers")},
        {id:"prods",i:"🧴",l:T(lang,"products")},
        {id:"stock",i:"📦",l:T(lang,"inventory")},
        {id:"comps",i:"🏢",l:T(lang,"companies")},
        ...(ca("beats")?[{id:"beats",i:"🗺️",l:T(lang,"beats")}]:[]),
        ...(ca("beats")?[{id:"dispatch",i:"📋",l:"Aaj Ka Beat"}]:[]),
      ]
    },
    {
      group:"Accounts",icon:"💸",
      items:[
        {id:"cashbank",i:"💰",l:"Cash & Bank"},
        ...(ca("expenses")?[{id:"expenses",i:"💸",l:T(lang,"expenses")}]:[]),
        ...(ca("cheques")?[{id:"cheques",i:"📝",l:T(lang,"cheques")}]:[]),
        {id:"loyalty",i:"🏆",l:T(lang,"loyalty")},
      ]
    },
    {
      group:"Reports",icon:"📈",
      items:[
        ...(ca("sales")?[{id:"sales",i:"📈",l:T(lang,"salesAnalysis")}]:[]),
        ...(ca("pl")?[{id:"pl",i:"💹",l:T(lang,"profitLoss")}]:[]),
        ...(ca("pl")?[{id:"bs",i:"📊",l:"Balance Sheet"}]:[]),
        ...(ca("pl")?[{id:"daybook",i:"📔",l:"Day Book"}]:[]),
        ...(ca("gst")?[{id:"bankrecon",i:"🏦",l:"Bank Reconciliation"}]:[]),
        ...(ca("gst")?[{id:"gst",i:"🏛️",l:T(lang,"gstReport")}]:[]),
        {id:"print",i:"🖨️",l:"Print Center"},
        {id:"dl",i:"📤",l:"Data & Export"},
      ]
    },
    {
      group:"Settings",icon:"⚙️",
      items:[
        {id:"usrs",i:"👤",l:T(lang,"staff")},
        {id:"plans",i:"💎",l:"Plans"},
        {id:"settings",i:"⚙️",l:T(lang,"settings")},
      ]
    },
  ];
  // Flat menu for compatibility
  const ADMIN_MENU=ADMIN_MENU_GROUPS.flatMap(g=>g.items);
  const STAFF_MENU=[
    {id:"dash",i:"📊",l:T(lang,"dashboard")},
    {id:"orders",i:"📦",l:T(lang,"orders")},
    {id:"staff_pays",i:"💰",l:T(lang,"payments")},
    {id:"custs",i:"👥",l:T(lang,"customers")},
    {id:"stock",i:"📦",l:T(lang,"inventory")},
    {id:"dispatch",i:"🗺️",l:"Mera Beat"},
  ];
  const DELIVERY_MENU=[
    {id:"deliveries",i:"🚚",l:"My Deliveries"},
    {id:"dash",i:"📊",l:"Dashboard"},
    {id:"dispatch",i:"🗺️",l:"Mera Beat"},
  ];
  const MENU=isA?ADMIN_MENU:isDB?DELIVERY_MENU:STAFF_MENU;

  // ── Auth Checks ───────────────────────────────────────────
  if(!user&&!superAdmin) return <Login onLogin={u=>{clearAllState();setUser(u);loadData(false,u);}} onSuperAdmin={u=>{setSuperAdmin(u);loadTenants();}}/>;
  if(superAdmin) return <SuperAdminPanel superAdmin={superAdmin} tenants={tenants} loadTenants={loadTenants} saTab={saTab} setSaTab={setSaTab} saForm={saForm} setSaForm={setSaForm} saMsg={saMsg} setSaMsg={setSaMsg} announcement={announcement} setAnnouncement={setAnnouncement} onLogout={()=>{setSuperAdmin(null);removeToken();}} showToast={showToast} fmt={fmt} today={today} todayStr={todayStr} C={C} Inp={Inp} Btn={Btn} Card={Card} API={API} impersonateTenant={impersonateTenant}/>;
  // ── Trial Expire Popup ────────────────────────────────────────
  if(trialExpired && user?.tenant_id){
    const hl=JSON.parse(localStorage.getItem("sa_helpline")||"{}");
    const pricing=JSON.parse(localStorage.getItem("sa_pricing")||"{}");
    const plans=[
      {id:"starter",name:"Starter",icon:"⭐",price:pricing.starter?.price||299,color:"#64748B"},
      {id:"basic",  name:"Basic",  icon:"🚀",price:pricing.basic?.price||499,  color:"#2563EB"},
      {id:"pro",    name:"Pro",    icon:"💎",price:pricing.pro?.price||999,    color:"#7C3AED"},
    ];
    return(
      <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#0F1C2E,#1C2B3A)",display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:"system-ui,sans-serif"}}>
        <div style={{width:"100%",maxWidth:420}}>
          {/* Logo */}
          <div style={{textAlign:"center",marginBottom:24}}>
            <div style={{width:60,height:60,borderRadius:16,background:"#F59E0B",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 10px"}}>💎</div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
              <svg width="90" height="90" viewBox="0 0 300 290" fill="none">
                <defs>
                  <radialGradient id="lldb" cx="50%" cy="30%" r="70%"><stop offset="0%" stop-color="#FFD700"/><stop offset="40%" stop-color="#FF8C00"/><stop offset="100%" stop-color="#CC2200"/></radialGradient>
                  <linearGradient id="llnb" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#1A3A6B"/><stop offset="100%" stop-color="#0A1F44"/></linearGradient>
                  <radialGradient id="llsg" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#FFFDE0"/><stop offset="50%" stop-color="#FFD700"/><stop offset="100%" stop-color="#FF8C00"/></radialGradient>
                  <clipPath id="llcc"><circle cx="150" cy="145" r="118"/></clipPath>
                  <linearGradient id="llg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#4CAF50"/><stop offset="100%" stop-color="#2E7D32"/></linearGradient>
                  <linearGradient id="llg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#FF7043"/><stop offset="100%" stop-color="#BF360C"/></linearGradient>
                  <linearGradient id="llg3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#42A5F5"/><stop offset="100%" stop-color="#1565C0"/></linearGradient>
                </defs>
                <circle cx="150" cy="145" r="124" fill="none" stroke="url(#lldb)" stroke-width="3"/>
                <g clip-path="url(#llcc)">
                  <rect x="32" y="27" width="236" height="118" fill="url(#lldb)"/>
                  <rect x="32" y="145" width="236" height="118" fill="url(#llnb)"/>
                  <g opacity="0.6"><line x1="150" y1="32" x2="150" y2="55" stroke="#FFD700" stroke-width="2.5" stroke-linecap="round"/><line x1="118" y1="38" x2="126" y2="58" stroke="#FFD700" stroke-width="2" stroke-linecap="round"/><line x1="182" y1="38" x2="174" y2="58" stroke="#FFD700" stroke-width="2" stroke-linecap="round"/><line x1="95" y1="55" x2="108" y2="70" stroke="#FFD700" stroke-width="1.5" stroke-linecap="round"/><line x1="205" y1="55" x2="192" y2="70" stroke="#FFD700" stroke-width="1.5" stroke-linecap="round"/></g>
                  <circle cx="150" cy="95" r="20" fill="url(#llsg)" opacity="0.4"/>
                  <circle cx="150" cy="95" r="13" fill="url(#llsg)"/>
                  <ellipse cx="90" cy="108" rx="22" ry="10" fill="rgba(255,200,100,0.35)"/>
                  <ellipse cx="210" cy="112" rx="20" ry="9" fill="rgba(255,200,100,0.3)"/>
                  <g fill="rgba(120,40,0,0.7)"><rect x="58" y="112" width="12" height="33"/><rect x="72" y="104" width="10" height="41"/><rect x="100" y="98" width="12" height="47"/><rect x="178" y="98" width="12" height="47"/><rect x="208" y="104" width="10" height="41"/><rect x="220" y="112" width="12" height="33"/></g>
                  <path d="M 32 143 Q 80 130 150 143 Q 220 156 268 143 L 268 158 Q 220 171 150 158 Q 80 145 32 158 Z" fill="rgba(255,255,255,0.85)"/>
                  <path d="M 218 168 Q 230 175 228 190 Q 218 205 205 200 Q 218 195 220 183 Q 222 170 218 168 Z" fill="rgba(220,230,255,0.85)"/>
                  <circle cx="190" cy="163" r="1.5" fill="#fff" opacity="0.8"/>
                  <circle cx="240" cy="168" r="1.5" fill="#fff" opacity="0.6"/>
                  <rect x="116" y="185" width="14" height="38" rx="3" fill="url(#llg1)"/>
                  <rect x="133" y="172" width="14" height="51" rx="3" fill="url(#llg2)"/>
                  <rect x="150" y="180" width="14" height="43" rx="3" fill="url(#llg3)"/>
                  <rect x="72" y="168" width="26" height="32" rx="3" fill="rgba(255,255,255,0.92)"/>
                  <line x1="77" y1="176" x2="93" y2="176" stroke="#90A4C0" stroke-width="1.5" stroke-linecap="round"/>
                  <line x1="77" y1="181" x2="93" y2="181" stroke="#90A4C0" stroke-width="1.5" stroke-linecap="round"/>
                  <circle cx="174" cy="205" r="13" fill="rgba(30,200,100,0.9)"/>
                  <polyline points="168,205 173,211 181,199" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                  <path d="M 32 238 Q 90 230 150 238 Q 210 246 268 238 L 268 263 L 32 263 Z" fill="rgba(8,30,70,0.6)"/>
                </g>
              </svg>
              <div style={{fontSize:22,fontWeight:900,color:"#fff",letterSpacing:"-0.5px"}}>
                <span style={{color:"#FF8C00"}}>Dawn</span><span>To</span><span>Dusk</span><span style={{color:"#FF8C00",fontSize:"17px"}}> Pro</span>
              </div>
            </div>
          </div>

          {/* Expire Card */}
          <div style={{background:"#fff",borderRadius:20,padding:24,boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}}>
            <div style={{textAlign:"center",marginBottom:20}}>
              <div style={{fontSize:40,marginBottom:8}}>⏰</div>
              <div style={{fontSize:18,fontWeight:800,color:"#EF4444",marginBottom:6}}>
                {trialDaysLeft<0?"Trial Expired!":"Trial Expiring Soon!"}
              </div>
              <div style={{fontSize:13,color:"#64748B",lineHeight:1.6}}>
                {trialDaysLeft<0
                  ?"Your free trial has ended. Please subscribe to continue using the app."
                  :`Your trial expires in just ${trialDaysLeft} days!`}
              </div>
            </div>

            {/* Plans */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:"#94A3B8",marginBottom:10,textAlign:"center"}}>SUBSCRIBE KAREIN</div>
              {plans.map(p=>(
                <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderRadius:10,border:`2px solid ${p.id==="basic"?p.color:"#E2E8F0"}`,background:p.id==="basic"?p.color+"08":"#fff",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:20}}>{p.icon}</span>
                    <div>
                      <div style={{fontWeight:700,fontSize:13,color:p.color}}>{p.name}</div>
                      {p.id==="basic"&&<div style={{fontSize:10,color:p.color,fontWeight:600}}>Most Popular</div>}
                    </div>
                  </div>
                  <div style={{fontWeight:800,fontSize:15,color:p.color}}>₹{p.price}<span style={{fontSize:10,fontWeight:400,color:"#94A3B8"}}>/mo</span></div>
                </div>
              ))}
            </div>

            {/* Contact */}
            <a href={`https://wa.me/91${(hl.whatsapp||hl.phone||"6284393851").replace(/\D/g,"")}`} target="_blank" rel="noreferrer"
              style={{display:"block",width:"100%",padding:"13px",borderRadius:12,background:"#25D366",color:"#fff",fontWeight:800,fontSize:14,textAlign:"center",textDecoration:"none",marginBottom:10,boxShadow:"0 4px 12px rgba(37,211,102,0.3)"}}>
              💬 WhatsApp Pe Subscribe Karo
            </a>
            <a href={`tel:${hl.phone||"6284393851"}`}
              style={{display:"block",width:"100%",padding:"12px",borderRadius:12,background:"#EFF6FF",color:"#2563EB",fontWeight:700,fontSize:13,textAlign:"center",textDecoration:"none",marginBottom:16}}>
              📞 Call: {hl.phone||"6284393851"}
            </a>

            {/* Trial warning only — allow access */}
            {trialDaysLeft>=0&&trialDaysLeft<=3&&<button onClick={()=>setTrialExpired(false)} style={{width:"100%",padding:"10px",borderRadius:10,background:"transparent",border:"1.5px solid #E2E8F0",color:"#94A3B8",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
              Continue with Trial ({trialDaysLeft} days left)
            </button>}

            <div style={{textAlign:"center",fontSize:11,color:"#94A3B8",marginTop:12}}>
              DawnToDusk Pro v2.0 • {hl.hours||"Mon-Sat 10AM-6PM"}
            </div>
          </div>
        </div>
      </div>
    );
  }
  if(subPage==="newOrder") return <NewOrderPage companies={companies} custs={custs} prods={prods} onSubmit={handleOrder} onCancel={()=>setSubPage(null)} printSettings={myCompany} canAccess={ca}/>;
  if(subPage==="newPur") return <NewPurPage companies={companies} supps={supps} prods={prods} onSubmit={handlePur} onCancel={()=>setSubPage(null)} printSettings={myCompany} canAccess={ca}/>;

  // ── Filtered Data ─────────────────────────────────────────
  const fOrders=filterByDate(orders,dateFrom,dateTo);
  const fPays=filterByDate(pays,dateFrom,dateTo);
  const fPurs=filterByDate(purs,dateFrom,dateTo);

  return(
    <div style={{fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",background:C.bg,minHeight:"100vh",display:"flex",flexDirection:"column",fontSize:"clamp(12px,2.5vw,16px)",maxWidth:"100vw"}}>
      <style>{`*{box-sizing:border-box!important}body,html{overflow-x:hidden!important;max-width:100vw!important}input,select,textarea{max-width:100%!important}
        .d2d-btn{transition:transform 0.12s ease,box-shadow 0.12s ease,filter 0.12s ease;box-shadow:0 1px 2px rgba(0,0,0,0.08);}
        .d2d-btn:hover:not(:disabled){filter:brightness(1.08);box-shadow:0 3px 8px rgba(0,0,0,0.14);transform:translateY(-1px);}
        .d2d-btn:active:not(:disabled){transform:translateY(0) scale(0.97);box-shadow:0 1px 2px rgba(0,0,0,0.08);filter:brightness(0.94);}
        .d2d-btn:focus-visible{outline:2px solid #2563EB;outline-offset:2px;}
        .d2d-card-click{transition:transform 0.15s ease,box-shadow 0.15s ease;}
        .d2d-card-click:hover{transform:translateY(-2px);box-shadow:0 6px 18px rgba(15,23,42,0.10);}
        .d2d-card-click:active{transform:translateY(0) scale(0.99);}
        @media (hover:none){.d2d-card-click:hover{transform:none;box-shadow:0 1px 3px rgba(15,23,42,0.06);}}
        .d2d-input{transition:border-color 0.15s ease,box-shadow 0.15s ease,background 0.15s ease;}
        .d2d-input:focus{border-color:#2563EB!important;box-shadow:0 0 0 3px rgba(37,99,235,0.10);background:#fff!important;}
        .d2d-bar-fill{transition:width 0.4s ease;}
        .d2d-modal-in{animation:d2dModalIn 0.18s ease;}
        @keyframes d2dModalIn{from{opacity:0;transform:translateY(10px) scale(0.97);}to{opacity:1;transform:translateY(0) scale(1);}}
        @media (hover:none){.d2d-btn:hover:not(:disabled){transform:none;filter:none;box-shadow:0 1px 2px rgba(0,0,0,0.08);}}
      `}</style>

      {toast&&<div style={{position:"fixed",top:12,left:"50%",transform:"translateX(-50%)",zIndex:9999,background:toast.t==="err"?C.danger:C.success,color:"#fff",padding:"10px 20px",borderRadius:10,fontSize:13,fontWeight:700,boxShadow:"0 4px 15px rgba(0,0,0,0.2)"}}>{toast.m}</div>}

      {/* TOP BAR — banners + header grouped into ONE sticky unit, so they all move
          together as a single piece instead of the header appearing to "settle"
          separately once the banners above it scroll away. */}
      <div ref={topBarRef} style={{position:"fixed",top:0,left:0,right:0,zIndex:200}}>
        {/* UPDATE AVAILABLE BANNER */}
        {updateAvailable&&<div style={{background:C.primary,color:"#fff",padding:"7px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
          <span style={{fontSize:11,fontWeight:600}}>🆕 Naya version aa gaya hai!</span>
          <button onClick={()=>window.location.reload()} style={{background:"#fff",color:C.primary,border:"none",padding:"4px 12px",borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>🔄 Refresh Karo</button>
        </div>}
        {/* OFFLINE BANNER */}
        {isOffline&&<div style={{background:"#78350F",color:"#fff",padding:"6px 14px",textAlign:"center",fontSize:11,fontWeight:600}}>
          📡 No internet — your changes are being saved on this device and will sync automatically once you're back online
        </div>}

        {/* TRIAL WARNING BANNER */}
        {tenantInfo&&trialDaysLeft!==null&&trialDaysLeft>=0&&trialDaysLeft<=7&&!trialExpired&&(
          <div style={{background:trialDaysLeft<=3?"#FEF2F2":"#FFF7ED",padding:"6px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`1px solid ${trialDaysLeft<=3?"#FECACA":"#FED7AA"}`}}>
            <div style={{fontSize:11,fontWeight:600,color:trialDaysLeft<=3?"#EF4444":"#D97706"}}>
              ⏰ Trial: {trialDaysLeft} days left!
            </div>
            <button onClick={()=>nav("plans")} style={{background:trialDaysLeft<=3?"#EF4444":"#D97706",border:"none",color:"#fff",padding:"3px 10px",borderRadius:6,cursor:"pointer",fontSize:10,fontWeight:700,fontFamily:"inherit"}}>
              Subscribe Now
            </button>
          </div>
        )}

        {sessionStorage.getItem("gd_sa_token_backup")&&<div style={{background:"#7C2D12",color:"#fff",padding:"6px 12px",display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11}}>
          <span>👁️ Support Mode — viewing as {user?.name}</span>
          <button onClick={returnToSuperAdmin} style={{background:"rgba(255,255,255,0.2)",border:"1px solid #fff",color:"#fff",padding:"3px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:700}}>← Return to Super Admin</button>
        </div>}

        {/* HEADER */}
        <div style={{background:C.side,padding:"0 12px",height:50,display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:"0 2px 8px rgba(0,0,0,0.15)"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
          <button onClick={()=>setMenuOpen(!menuOpen)} style={{background:"rgba(255,255,255,0.1)",border:"none",color:"#fff",width:33,height:33,borderRadius:8,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>{menuOpen?"✕":"☰"}</button>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <svg width="36" height="36" viewBox="0 0 300 290" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="ldb" cx="50%" cy="30%" r="70%"><stop offset="0%" stop-color="#FFD700"/><stop offset="40%" stop-color="#FF8C00"/><stop offset="100%" stop-color="#CC2200"/></radialGradient>
    <linearGradient id="lnb" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#1A3A6B"/><stop offset="100%" stop-color="#0A1F44"/></linearGradient>
    <radialGradient id="lsg" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#FFFDE0"/><stop offset="50%" stop-color="#FFD700"/><stop offset="100%" stop-color="#FF8C00"/></radialGradient>
    <clipPath id="lcc"><circle cx="150" cy="145" r="118"/></clipPath>
    <linearGradient id="lg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#4CAF50"/><stop offset="100%" stop-color="#2E7D32"/></linearGradient>
    <linearGradient id="lg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#FF7043"/><stop offset="100%" stop-color="#BF360C"/></linearGradient>
    <linearGradient id="lg3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#42A5F5"/><stop offset="100%" stop-color="#1565C0"/></linearGradient>
  </defs>
  <circle cx="150" cy="145" r="124" fill="none" stroke="url(#ldb)" stroke-width="3"/>
  <g clip-path="url(#lcc)">
    <rect x="32" y="27" width="236" height="118" fill="url(#ldb)"/>
    <rect x="32" y="145" width="236" height="118" fill="url(#lnb)"/>
    <line x1="150" y1="32" x2="150" y2="55" stroke="#FFD700" stroke-width="2.5" stroke-linecap="round" opacity="0.6"/>
    <line x1="118" y1="38" x2="126" y2="58" stroke="#FFD700" stroke-width="2" stroke-linecap="round" opacity="0.6"/>
    <line x1="182" y1="38" x2="174" y2="58" stroke="#FFD700" stroke-width="2" stroke-linecap="round" opacity="0.6"/>
    <circle cx="150" cy="95" r="20" fill="url(#lsg)" opacity="0.4"/>
    <circle cx="150" cy="95" r="13" fill="url(#lsg)"/>
    <ellipse cx="90" cy="108" rx="22" ry="10" fill="rgba(255,200,100,0.35)"/>
    <ellipse cx="210" cy="112" rx="20" ry="9" fill="rgba(255,200,100,0.3)"/>
    <g fill="rgba(120,40,0,0.7)">
      <rect x="58" y="112" width="12" height="33"/>
      <rect x="72" y="104" width="10" height="41"/>
      <rect x="100" y="98" width="12" height="47"/>
      <rect x="178" y="98" width="12" height="47"/>
      <rect x="208" y="104" width="10" height="41"/>
      <rect x="220" y="112" width="12" height="33"/>
    </g>
    <path d="M 32 143 Q 80 130 150 143 Q 220 156 268 143 L 268 158 Q 220 171 150 158 Q 80 145 32 158 Z" fill="rgba(255,255,255,0.85)"/>
    <path d="M 218 168 Q 230 175 228 190 Q 218 205 205 200 Q 218 195 220 183 Q 222 170 218 168 Z" fill="rgba(220,230,255,0.85)"/>
    <circle cx="190" cy="163" r="1.5" fill="#fff" opacity="0.8"/>
    <circle cx="240" cy="168" r="1.5" fill="#fff" opacity="0.6"/>
    <rect x="116" y="185" width="14" height="38" rx="3" fill="url(#lg1)"/>
    <rect x="133" y="172" width="14" height="51" rx="3" fill="url(#lg2)"/>
    <rect x="150" y="180" width="14" height="43" rx="3" fill="url(#lg3)"/>
    <rect x="72" y="168" width="26" height="32" rx="3" fill="rgba(255,255,255,0.92)"/>
    <line x1="77" y1="176" x2="93" y2="176" stroke="#90A4C0" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="77" y1="181" x2="93" y2="181" stroke="#90A4C0" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="174" cy="205" r="13" fill="rgba(30,200,100,0.9)"/>
    <polyline points="168,205 173,211 181,199" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <path d="M 32 238 Q 90 230 150 238 Q 210 246 268 238 L 268 263 L 32 263 Z" fill="rgba(8,30,70,0.6)"/>
  </g>
</svg>
            <div style={{display:"flex",flexDirection:"column",lineHeight:1.2}}>
              <div style={{fontSize:13,fontWeight:800,color:"#fff"}}>{myCompany?.name||"DawnToDusk Pro"}</div>
              <div style={{fontSize:8,color:"rgba(255,255,255,0.4)",fontWeight:600,letterSpacing:"1px"}}>DISTRIBUTION PRO</div>
            </div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
          {/* Notification Bell */}
          <button onClick={()=>setShowNotifPanel(v=>!v)} style={{position:"relative",background:"rgba(255,255,255,0.12)",border:"none",color:"#fff",padding:"4px 7px",borderRadius:8,cursor:"pointer",fontSize:14,flexShrink:0}}>
            🔔
            {unreadCount>0&&<span style={{position:"absolute",top:-2,right:-2,background:C.danger,color:"#fff",fontSize:8,fontWeight:800,borderRadius:10,padding:"1px 4px",minWidth:14,textAlign:"center"}}>{unreadCount>9?"9+":unreadCount}</span>}
          </button>
          {/* Stock Alert - small badge, always visible */}
          {lowS.length>0&&isA&&<button onClick={()=>nav("stock")} style={{background:C.danger,border:"none",color:"#fff",padding:"2px 6px",borderRadius:10,cursor:"pointer",fontSize:9,fontWeight:800,flexShrink:0}}>⚠️{lowS.length}</button>}
          {/* User name - truncated on small screens */}
          <div style={{background:"rgba(255,255,255,0.1)",borderRadius:14,padding:"3px 7px",display:"flex",alignItems:"center",gap:3,maxWidth:90,overflow:"hidden"}}>
            <span style={{fontSize:10}}>{isA?"👑":"👤"}</span>
            <span style={{fontSize:10,color:"#fff",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.name}</span>
          </div>
          {/* Install - only show if space available */}
          {showInstall&&<button onClick={async()=>{if(deferredPrompt){deferredPrompt.prompt();const{outcome}=await deferredPrompt.userChoice;if(outcome==='accepted'){setShowInstall(false);setDeferredPrompt(null);};}}} style={{background:"rgba(99,102,241,0.2)",border:"1px solid #6366F1",color:"#6366F1",padding:"2px 5px",borderRadius:5,cursor:"pointer",fontSize:9,fontWeight:700,flexShrink:0}}>📲</button>}
          {/* Refresh */}
          <button onClick={async()=>{showToast("🔄 Refreshing...");await loadData();}} style={{background:"rgba(37,99,235,0.15)",border:`1px solid ${C.primary}`,color:C.primary,padding:"2px 6px",borderRadius:5,cursor:"pointer",fontSize:10,fontWeight:600,flexShrink:0}}>🔄</button>
        </div>
      </div>
      </div>
      <div style={{height:topBarHeight,flexShrink:0}}/>

      {/* NOTIFICATION PANEL */}
      {showNotifPanel&&<div style={{position:"fixed",inset:0,zIndex:250}} onClick={()=>setShowNotifPanel(false)}>
        <div style={{position:"absolute",top:54,right:8,width:"min(340px,92vw)",maxHeight:"70vh",background:"#fff",borderRadius:12,boxShadow:"0 8px 30px rgba(0,0,0,0.25)",overflow:"hidden",display:"flex",flexDirection:"column"}} onClick={e=>e.stopPropagation()}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:C.side,color:"#fff"}}>
            <span style={{fontWeight:700,fontSize:13}}>🔔 Notifications</span>
            {unreadCount>0&&<button onClick={markAllNotifsRead} style={{background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",fontSize:10,padding:"3px 8px",borderRadius:6,cursor:"pointer"}}>Mark all read</button>}
          </div>
          <div style={{overflowY:"auto",flex:1}}>
            {notifications.length===0&&<div style={{padding:24,textAlign:"center",color:C.sub,fontSize:12}}>No notifications yet</div>}
            {notifications.map(n=>(
              <div key={n.id} onClick={()=>{markNotifRead(n.id);if(n.link)nav(n.link);setShowNotifPanel(false);}} style={{padding:"10px 14px",borderBottom:`1px solid ${C.border}`,cursor:"pointer",background:n.is_read?"#fff":"#EFF6FF"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <span style={{fontWeight:700,fontSize:12}}>{n.title}</span>
                  {!n.is_read&&<span style={{width:7,height:7,borderRadius:10,background:C.primary,flexShrink:0,marginTop:3}}/>}
                </div>
                <div style={{fontSize:11,color:C.sub,marginTop:2}}>{n.message}</div>
                <div style={{fontSize:9,color:C.sub,marginTop:3}}>{new Date(n.created_at).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
              </div>
            ))}
          </div>
        </div>
      </div>}

      {/* SIDEBAR */}
      {menuOpen&&<div style={{position:"fixed",inset:0,zIndex:150}} onClick={()=>setMenuOpen(false)}>
        <div style={{position:"absolute",left:0,top:50,bottom:60,width:230,background:C.side,overflowY:"auto",padding:"8px 0",boxShadow:"4px 0 20px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
          {(isA?ADMIN_MENU_GROUPS:[{group:"Menu",icon:"📋",items:MENU}]).map(grp=>(
            grp.items.length===0?null:
            <div key={grp.group}>
              <button onClick={()=>toggleGroup(grp.group)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px 6px",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit"}}>
                <div style={{fontSize:9,fontWeight:800,color:"rgba(255,255,255,0.5)",textTransform:"uppercase",letterSpacing:1.5}}>{grp.icon} {grp.group}</div>
                <span style={{color:"rgba(255,255,255,0.4)",fontSize:10}}>{expandedGroups[grp.group]?"▼":"▶"}</span>
              </button>
              {expandedGroups[grp.group]&&grp.items.map(m=>(
                <button key={m.id} onClick={()=>nav(m.id)} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 16px 9px 24px",background:page===m.id?"rgba(255,255,255,0.15)":"transparent",border:"none",color:page===m.id?"#fff":C.sideT,cursor:"pointer",fontSize:13,fontWeight:page===m.id?700:400,textAlign:"left",fontFamily:"inherit",borderLeft:page===m.id?`3px solid ${C.primary}`:"3px solid transparent"}}>
                  <span style={{fontSize:15}}>{m.i}</span>
                  <span style={{flex:1}}>{m.l}</span>
                  {m.id==="stock"&&lowS.length>0&&<span style={{fontSize:8,padding:"1px 5px",borderRadius:7,background:C.danger,color:"#fff"}}>{lowS.length}</span>}
                  {page===m.id&&<span style={{fontSize:10,color:C.primary}}>●</span>}
                </button>
              ))}
            </div>
          ))}
          <div style={{margin:"8px 16px",borderTop:`1px solid rgba(255,255,255,0.1)`,paddingTop:8}}>
            <div style={{fontSize:9,color:"#475569",marginBottom:10}}>{user.name} • {user.role}</div>
            <button onClick={()=>{const nl=lang==="en"?"hi":"en";setLang(nl);localStorage.setItem("gd_lang",nl);}} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 8px",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,color:"#fff",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit",marginBottom:8}}>
              <span style={{fontSize:15}}>🌐</span> {lang==="en"?"हिंदी में बदलें":"Switch to English"}
            </button>
            <a href="tel:916284393851" style={{display:"flex",alignItems:"center",gap:10,padding:"10px 8px",background:"rgba(16,185,129,0.15)",border:"1px solid rgba(16,185,129,0.3)",borderRadius:8,color:"#10B981",textDecoration:"none",fontSize:13,fontWeight:600,marginBottom:8}}>
              <span style={{fontSize:15}}>📞</span> Help / Support
            </a>
            <button onClick={()=>{if(window.confirm("Logout karna hai?")){clearAllState();setUser(null);setPage("dash");removeToken();}}} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 8px",background:"rgba(239,68,68,0.15)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,color:C.danger,cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit"}}>
              <span style={{fontSize:15}}>🚪</span> {T(lang,"logout")}
            </button>
          </div>
        </div>
      </div>}

      {/* MAIN CONTENT */}
      <div style={{flex:1,padding:"12px 12px 80px",maxWidth:"min(900px,100%)",margin:"0 auto",width:"100%",boxSizing:"border-box",overflowX:"hidden"}} onClick={()=>menuOpen&&setMenuOpen(false)}>
        {/* All page content is imported from pages */}
        <PageContent
          page={page} nav={nav} isA={isA} isDB={isDB} user={user}
          lang={lang} T={T} C={C} fmt={fmt} today={today} todayStr={todayStr}
          monthStart={monthStart} monthEnd={monthEnd} fyStart={fyStart} fyEnd={fyEnd}
          last7={last7} last30={last30} filterByDate={filterByDate} getFYList={getFYList}
          gCalc={gCalc} dlCSV={dlCSV} numToWords={numToWords}
          companies={companies} setCompanies={setCompanies}
          custs={custs} setCusts={setCusts}
          supps={supps} setSupps={setSupps}
          prods={prods} setProds={setProds}
          orders={orders} setOrders={setOrders}
          pays={pays} setPays={setPays}
          purs={purs} setPurs={setPurs}
          notes={notes} setNotes={setNotes}
          users={users} setUsers={setUsers}
          beats={beats} setBeats={setBeats}
          expenses={expenses} setExpenses={setExpenses}
          cheques={cheques} setCheques={setCheques}
          modal={modal} setModal={setModal}
          tenantPlan={tenantPlan} tenantInfo={tenantInfo} pricing={pricing} canAccess={(f)=>canAccess(tenantPlan,f)} getPlanLimit={(k)=>getPlanLimit(tenantPlan,k)}
          form={form} setForm={setForm}
          payOid={payOid} setPayOid={setPayOid} payForm={payForm} setPayForm={setPayForm}
          nItems={nItems} setNItems={setNItems}
          payAmt={payAmt} setPayAmt={setPayAmt}
          payMethod2={payMethod2} setPayMethod2={setPayMethod2}
          payingCust={payingCust} setPayingCust={setPayingCust}
          dbOtp={dbOtp} setDbOtp={setDbOtp}
          dbOtpOrderId={dbOtpOrderId} setDbOtpOrderId={setDbOtpOrderId}
          dateFrom={dateFrom} setDateFrom={setDateFrom}
          dateTo={dateTo} setDateTo={setDateTo}
          gTab={gTab} setGTab={setGTab}
          selFY={selFY} setSelFY={setSelFY}
          cf={cf} setCf={setCf}
          prev={prev} setPrev={setPrev}
          aiInsight={aiInsight} aiLoading={aiLoading} aiPurSug={aiPurSug} setAiPurSug={setAiPurSug}
          myCompany={myCompany} setMyCompany={setMyCompany}
          fOrders={fOrders} fPays={fPays} fPurs={fPurs}
          lowS={lowS} tSales={tSales} tPaid={tPaid} tDue={tDue} expiringBatches={expiringBatches} batches={batches} offers={offers} suppPays={suppPays}
          loadData={loadData} showToast={showToast}
          handleOrder={handleOrder} handlePur={handlePur}
          addPay={addPay} setSubPage={setSubPage}
          getAIInsights={getAIInsights}
          getSmartPurchaseSuggestions={getSmartPurchaseSuggestions}
          sendDailyReport={sendDailyReport}
          sendPaymentReminder={sendPaymentReminder}
          gc={gc} API={API}
          pInv={pInv} pInvBulk={pInvBulk} pPur={pPur} pNote={pNote} pReceipt={pReceipt} waShare={waShare}
          accounts={accounts} setAccounts={setAccounts}
          Bdg={Bdg} CB={CB} Card={Card} Sc={Sc} Row={Row}
          Btn={Btn} Inp={Inp} Sel={Sel} Bar={Bar} Modal={Modal}
          AddProdToInvoice={AddProdToInvoice} InvModal={InvModal}
          BankRecon={BankRecon} PhotoScanModal={PhotoScanModal}
          NC={NC} NI={NI} SC={SC} CC={CC}
          editComp={editComp} setEditComp={setEditComp}
        />
      </div>

      {/* ── Bottom Tab Bar ── */}
      {user&&!subPage&&<div style={{
        position:"fixed",bottom:0,left:0,right:0,height:56,
        background:C.side,display:"flex",alignItems:"center",
        justifyContent:"space-around",zIndex:100,
        boxShadow:"0 -2px 12px rgba(0,0,0,0.2)",
        borderTop:"1px solid rgba(255,255,255,0.08)"
      }}>
        {[
          {id:"dash",i:"📊",l:"Dashboard"},
          {id:"orders",i:"📦",l:"Orders"},
          {id:"pays",i:"💰",l:"Payments"},
        ].map(tab=>(
          <button key={tab.id} onClick={()=>nav(tab.id)} style={{
            flex:1,display:"flex",flexDirection:"column",alignItems:"center",
            gap:2,background:"none",border:"none",cursor:"pointer",
            padding:"6px 0",fontFamily:"inherit"
          }}>
            <span style={{fontSize:20}}>{tab.i}</span>
            <span style={{fontSize:9,fontWeight:page===tab.id?800:400,color:page===tab.id?"#fff":"rgba(255,255,255,0.5)"}}>{tab.l}</span>
            {page===tab.id&&<div style={{width:20,height:2,background:C.primary,borderRadius:2}}/>}
          </button>
        ))}
      </div>}

    </div>
  );
}
